/**
 * The two strings the comment layer builds rather than renders directly.
 *
 * Both are shared by the composer and the thread card, and both are the kind of
 * thing that drifts into two versions the moment they are inlined twice — one
 * card would truncate at a different length, one label would say "deleted" where
 * the other said "removed". They are also the only text in this feature worth a
 * test, for the reason {@link orphanLabel} carries: §8.1 requires the label, and
 * a comment rendered without it is a comment that appears to be about the words
 * currently under it.
 */

import type { OrphanReason } from './types.ts';

/** Long enough to recognise the sentence, short enough not to become the card. */
const QUOTE_LIMIT = 180;

export function quoted(text: string): string {
  const collapsed = text.replace(/\s+/g, ' ').trim();
  return collapsed.length > QUOTE_LIMIT ? `${collapsed.slice(0, QUOTE_LIMIT)}…` : collapsed;
}

/**
 * §8.1's "labeled as referring to removed content", split by which removal
 * happened — the two read differently to the person who wrote the comment.
 */
/**
 * §8.1's label for a block-level comment whose block has been rewritten.
 *
 * Separate from {@link orphanLabel} because this comment is not orphaned and
 * must not read as though it were: nothing was removed, it is still anchored,
 * and it is still in the margin. What changed is the text underneath it, and
 * saying so is the difference between a record and a misattribution.
 */
export function blockChangedLabel(): string {
  return 'This block has been edited since the comment was written, so it may no longer be about what it says now.';
}

export function orphanLabel(reason: OrphanReason | null): string {
  if (reason === 'block_deleted') {
    return 'The block this was anchored to has been deleted, so this comment is kept as a record.';
  }
  if (reason === 'text_changed') {
    return 'The text this quotes has been edited away, so this comment is kept as a record.';
  }
  // The API sends a reason with every orphaned comment. This is the sentence for
  // a state that arrives without one — a blank line where §8.1's label belongs
  // is the one outcome this function may not have.
  return 'This refers to content that is no longer in the document.';
}
